import { PORTAL_DEFAULT_FONT, type PortalDesign } from "@axl/membership-sdk";

import { DEFAULT_PRIMARY, FONT_FAMILY, type AntLook } from "../theme/antTheme";

/**
 * Writes the portal design's typography, shape and width onto <html> as `--axl-*` tokens (the defaults in
 * `styles/tokens.scss` are the «default» values below) and loads the chosen Google Fonts. Called from the
 * app root whenever the design changes; antd gets the same values through `designAntLook`.
 */
const FALLBACK_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif';
const SERIF_FONTS = new Set(["Lora", "PT Serif", "Merriweather", "Playfair Display"]);

const isSystem = (font: string) => font.toLowerCase() === "system";
/** Inter ships with the app (`@fontsource-variable/inter`), the OS font needs nothing. */
const needsDownload = (font: string) => !isSystem(font) && font !== PORTAL_DEFAULT_FONT;
const safeFamily = (font: string) => font.replace(/["\\;{}<>]/g, "").trim();

export function fontStack(font: string): string {
  if (font === PORTAL_DEFAULT_FONT) return FONT_FAMILY;
  if (isSystem(font)) return FALLBACK_STACK;
  const family = safeFamily(font);
  return SERIF_FONTS.has(family) ? `"${family}", Georgia, "Times New Roman", serif` : `"${family}", ${FALLBACK_STACK}`;
}

const FONT_LINK_ID = "axl-portal-fonts";

function loadGoogleFonts(fonts: string[]) {
  const families = Array.from(new Set(fonts.filter(needsDownload).map(safeFamily))).filter(Boolean);
  let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!families.length) {
    link?.remove();
    return;
  }
  const query = families.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700`).join("&");
  const href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  if (link?.href === href) return;
  if (!document.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"]')) {
    const pre = document.createElement("link");
    pre.rel = "preconnect";
    pre.href = "https://fonts.gstatic.com";
    pre.crossOrigin = "anonymous";
    document.head.appendChild(pre);
  }
  if (!link) {
    link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = href;
}

type Scale = PortalDesign["typography"]["scale"];
const TEXT: Record<Scale, Record<string, number>> = {
  compact: { xs: 11, sm: 12, md: 14, lg: 16, xl: 19, "2xl": 22, "3xl": 28, reading: 16 },
  default: { xs: 12, sm: 13, md: 15, lg: 17, xl: 20, "2xl": 24, "3xl": 30, reading: 17 },
  large: { xs: 13, sm: 14, md: 16, lg: 18, xl: 21, "2xl": 26, "3xl": 32, reading: 18 },
};

type Radius = PortalDesign["shape"]["radius"];
const RADIUS: Record<Radius, { sm: number; base: number; lg: number; xl: number }> = {
  sharp: { sm: 3, base: 4, lg: 6, xl: 8 },
  default: { sm: 8, base: 12, lg: 16, xl: 20 },
  rounded: { sm: 12, base: 20, lg: 24, xl: 28 },
};

export const designAntLook = (design: PortalDesign): AntLook => ({
  fontFamily: fontStack(design.typography.font),
  fontSize: TEXT[design.typography.scale].md ?? 15,
  radius: design.shape.radius,
  buttonColor: design.brand.buttonColor,
  highContrast: design.accessibility.highContrast,
  reduceMotion: design.accessibility.reduceMotion,
});

/** The accent of a theme: design `brand.darkPrimaryColor` in the dark one when set, else the school's colour. */
export const accentFor = (design: PortalDesign, primary: string | null | undefined, resolved: "light" | "dark") =>
  resolved === "dark" && design.brand.darkPrimaryColor ? design.brand.darkPrimaryColor : primary;

/**
 * Root switches read by global SCSS (`styles/design-switches.scss`): primary-button style, high contrast,
 * reduced motion. Absent attribute = today's look.
 */
function applyRootSwitches(design: PortalDesign) {
  const root = document.documentElement;
  const toggle = (name: string, value: string | null) => (value ? root.setAttribute(name, value) : root.removeAttribute(name));
  toggle("data-btn-style", design.brand.buttonStyle === "filled" ? null : design.brand.buttonStyle);
  toggle("data-contrast", design.accessibility.highContrast ? "high" : null);
  toggle("data-motion", design.accessibility.reduceMotion ? "reduce" : null);
}

export function applyDesignTokens(design: PortalDesign) {
  const style = document.documentElement.style;
  const set = (name: string, value: string) => style.setProperty(name, value);

  set("--axl-font", fontStack(design.typography.font));
  set("--axl-font-heading", fontStack(design.typography.headingFont));
  for (const [key, px] of Object.entries(TEXT[design.typography.scale])) {
    set(key === "reading" ? "--axl-reading" : `--axl-text-${key}`, `${px}px`);
  }
  const r = RADIUS[design.shape.radius];
  set("--axl-radius-sm", `${r.sm}px`);
  set("--axl-radius", `${r.base}px`);
  set("--axl-radius-lg", `${r.lg}px`);
  set("--axl-radius-xl", `${r.xl}px`);
  set("--axl-reading-max", design.lesson.width === "wide" ? "1040px" : "760px");
  applyRootSwitches(design);

  loadGoogleFonts([design.typography.font, design.typography.headingFont]);
}

// ---------- header colours ----------
const toRgb = (hex: string): [number, number, number] | null => {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

const luminance = ([r, g, b]: [number, number, number]) => {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

const DARK_TEXT = "#151922";
const LIGHT_TEXT = "#ffffff";

/**
 * The coloured header bar (style `accent` / `custom`): its background and a readable foreground — white or
 * near-black, whichever contrasts more (WCAG ratio). `null` = the plain surface bar (style `surface`).
 */
export function headerColors(
  design: PortalDesign,
  primary: string | null | undefined,
): { bg: string; fg: string; tone: "light" | "dark" } | null {
  const style = design.header.style;
  if (style === "surface") return null;
  const bg = style === "custom" ? design.header.color : (primary && toRgb(primary) ? primary : DEFAULT_PRIMARY);
  const rgb = bg ? toRgb(bg) : null;
  if (!bg || !rgb) return null;
  const l = luminance(rgb);
  const onLight = contrast(l, luminance(toRgb(DARK_TEXT)!));
  const onDark = contrast(l, 1);
  return onDark >= onLight ? { bg, fg: LIGHT_TEXT, tone: "dark" } : { bg, fg: DARK_TEXT, tone: "light" };
}
